/**
 * Marketing Phase 0 — P0-4: storefront campaign-touch capture.
 *
 * Reporting hints only. Nothing here changes what the storefront shows,
 * prices, or allows; the server re-validates everything
 * (`backend/services/marketing/attribution.py`) and ignores it entirely
 * unless attribution is enabled for the business.
 *
 * Storage is `sessionStorage["retail_attribution_v1:" + slug]` — scoped
 * to the storefront slug, like `retailSessionKeys.js`, so a touch never
 * follows the customer to a different business in the same tab. Every
 * storage access is wrapped: with storage unavailable the storefront and
 * checkout behave exactly as before, just without attribution.
 *
 * Normalization (mirrors the server; documented there in full):
 *   - only utm_source, utm_medium, utm_campaign, utm_content, utm_term
 *     and mb_cid are read; every other query parameter is ignored;
 *   - values are trimmed, 1-100 chars of [A-Za-z0-9._~+\- ], and dropped
 *     when they look like an email address or a phone number;
 *   - utm_source / utm_medium / utm_campaign are lowercased (labels);
 *     utm_content / utm_term keep their case (may be opaque ids);
 *   - mb_cid is opaque: kept only if it already matches
 *     ^mbc_[a-z0-9]{10,32}$, never altered;
 *   - only the landing PATH is kept — never the query string.
 * Touch model: first_touch is set once; a new valid campaign landing
 * replaces last_touch; a direct visit (no valid campaign value) changes
 * nothing.
 */

const ATTRIBUTION_KEY = "retail_attribution_v1";
const CLIENT_MODE_KEY = "retail_client_mode_v1";

export const CAMPAIGN_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term", "mb_cid"];
const LABEL_KEYS = new Set(["utm_source", "utm_medium", "utm_campaign"]);
const MAX_VALUE_LEN = 100;
const VALUE_RE = /^[A-Za-z0-9._~+\- ]+$/;
const MB_CID_RE = /^mbc_[a-z0-9]{10,32}$/;
const LINK_SLUG_RE = /^[A-Za-z0-9][A-Za-z0-9_-]{0,63}$/;
const PATH_RE = /^\/retail\/[A-Za-z0-9_-]{1,80}(?:\/[A-Za-z0-9_-]{1,80})?\/?$/;
const PHONE_ONLY_RE = /^[0-9 ()+.-]+$/;

export function attributionKey(slug) {
  return `${ATTRIBUTION_KEY}:${slug || "unknown"}`;
}

function clientModeKey(slug) {
  return `${CLIENT_MODE_KEY}:${slug || "unknown"}`;
}

export function looksLikeContact(value) {
  const lower = value.toLowerCase();
  if (value.includes("@") || lower.includes("%40") || lower.includes("mailto")) return true;
  const digits = (value.match(/\d/g) || []).length;
  if (PHONE_ONLY_RE.test(value) && digits >= 7 && digits <= 15) return true;
  return hasPhoneLengthDigitRun(value);
}

// A run of digits, optionally separated by single spaces, dots or dashes,
// holding 10-15 digits in total (no lookbehind regex, so older Safari
// can still parse this bundle). Longer runs (e.g. an 18-digit ad id)
// are not phone numbers.
function hasPhoneLengthDigitRun(value) {
  let digits = 0;
  let pendingSep = false;
  const flush = () => digits >= 10 && digits <= 15;
  for (let i = 0; i < value.length; i += 1) {
    const ch = value[i];
    if (ch >= "0" && ch <= "9") {
      digits += 1;
      pendingSep = false;
    } else if ((ch === " " || ch === "." || ch === "-") && digits > 0 && !pendingSep) {
      pendingSep = true;
    } else {
      if (flush()) return true;
      digits = 0;
      pendingSep = false;
    }
  }
  return flush();
}

export function normalizeValue(key, raw) {
  if (typeof raw !== "string") return null;
  if (key === "mb_cid") return MB_CID_RE.test(raw) ? raw : null;
  const value = raw.trim();
  if (!value || value.length > MAX_VALUE_LEN || !VALUE_RE.test(value)) return null;
  if (looksLikeContact(value)) return null;
  return LABEL_KEYS.has(key) ? value.toLowerCase() : value;
}

/** A touch from a query string, or null for a direct visit. */
export function touchFromLanding({ search, pathname, slug, linkSlug, now = new Date() }) {
  let params;
  try {
    params = new URLSearchParams(search || "");
  } catch (_e) {
    return null;
  }
  const touch = {};
  for (const key of CAMPAIGN_KEYS) {
    if (!params.has(key)) continue;
    const value = normalizeValue(key, params.get(key));
    if (value !== null) touch[key] = value;
  }
  if (Object.keys(touch).length === 0) return null;
  const path = typeof pathname === "string" ? pathname : "";
  const parts = path.split("/").filter(Boolean);
  if (PATH_RE.test(path) && path.length <= 200 && parts[1] === slug) touch.landing_path = path;
  if (typeof linkSlug === "string" && LINK_SLUG_RE.test(linkSlug)) touch.link_slug = linkSlug;
  touch.captured_at = now.toISOString();
  return touch;
}

export function loadAttribution(slug) {
  try {
    const raw = sessionStorage.getItem(attributionKey(slug));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    const out = {};
    if (parsed.first_touch && typeof parsed.first_touch === "object") out.first_touch = parsed.first_touch;
    if (parsed.last_touch && typeof parsed.last_touch === "object") out.last_touch = parsed.last_touch;
    return Object.keys(out).length ? out : null;
  } catch (_e) {
    return null;
  }
}

/**
 * Record a landing for THIS slug. Returns the stored envelope (or null).
 * Never throws; a direct visit leaves existing touches untouched.
 */
export function captureLanding({ search, pathname, slug, linkSlug, now }) {
  try {
    if (!slug) return null;
    const touch = touchFromLanding({ search, pathname, slug, linkSlug, now });
    const existing = loadAttribution(slug) || {};
    if (!touch) return Object.keys(existing).length ? existing : null;
    const next = {
      first_touch: existing.first_touch || touch,
      last_touch: touch,
    };
    sessionStorage.setItem(attributionKey(slug), JSON.stringify(next));
    return next;
  } catch (_e) {
    return null;
  }
}

/** `?kiosk=1` tablet mode — remembered per slug for the checkout hint. */
export function saveClientMode(slug, mode) {
  try {
    if (slug && mode === "kiosk") sessionStorage.setItem(clientModeKey(slug), "kiosk");
  } catch (_e) { /* noop — hint only */ }
}

export function clientModeField(slug) {
  try {
    return sessionStorage.getItem(clientModeKey(slug)) === "kiosk" ? { client_mode: "kiosk" } : {};
  } catch (_e) {
    return {};
  }
}
